import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Switch } from '../ui/switch';
import { Loader2, ClipboardList, User, Briefcase } from 'lucide-react';
import JobPicker from '../jobs/JobPicker';

const CANDIDATE_STATUSES = [
  'Not Responding',
  'Switch Off',
  'Call Back',
  'Number Busy',
  'Not Selected',
  'Not Interested',
  'Recently got a job',
  'Not Reachable',
  'Invalid No',
  'Looking for WFH',
  'Call Disconnected',
  'Selected'
];

const WorkbenchDialog = ({
  isOpen,
  onClose,
  candidate,
  remark,
  setRemark,
  status,
  setStatus,
  isPrivate,
  setIsPrivate,
  loading,
  onSubmit,
  statuses = CANDIDATE_STATUSES,
  // NEW (iter_38): optional pipeline shortlist
  jobs = [],
  pipelineJobId = '',
  setPipelineJobId = () => {},
}) => {
  if (!candidate) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5" />
            Add to Workbench
          </DialogTitle>
          <DialogDescription>
            Save a personal status note for {candidate?.name || 'this candidate'} — and optionally also push them to a job's Live Pipeline so they show up in your Command Center.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 overflow-y-auto flex-1 min-h-0 pr-1">
          {/* Candidate Preview */}
          <div className="p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-teal-700" />
              </div>
              <div>
                <p className="font-medium text-slate-900">
                  {candidate?.name || 'Unnamed Candidate'}
                </p>
                <p className="text-sm text-slate-500">
                  {candidate?.email || 'No email'}
                </p>
                {candidate?.current_company && (
                  <p className="text-xs text-slate-400">
                    {candidate?.current_designation || 'N/A'} at {candidate.current_company}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Candidate Status *</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="workbench-status-select">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {statuses.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Additional Remarks (Optional)</Label>
            <Textarea
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder="Add any additional notes about this candidate..."
              rows={3}
              data-testid="workbench-remark-input"
            />
          </div>

          {/* NEW (iter_38) — Optional shortlist into a job's pipeline */}
          <div className="space-y-2 rounded-lg border border-teal-200 bg-teal-50/40 p-3">
            <Label className="flex items-center gap-1.5 text-teal-800">
              <Briefcase className="w-4 h-4" />
              Also add to a job's Live Pipeline (optional)
            </Label>
            <p className="text-xs text-slate-500">
              Pick a job to immediately shortlist this candidate to its <strong>Sourced</strong> stage, so they appear in your <strong>Command Center</strong>. Leave as <em>"None — workbench only"</em> to just save the personal note.
            </p>
            <JobPicker
              jobs={jobs}
              value={pipelineJobId || ''}
              onChange={(v) => setPipelineJobId(v)}
              placeholder="Pick a job…"
              testid="workbench-pipeline-job"
              allowNone
              noneLabel="None — workbench only"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="workbench-private"
              checked={isPrivate}
              onCheckedChange={setIsPrivate}
            />
            <Label htmlFor="workbench-private" className="text-sm cursor-pointer">
              Private (only visible to you)
            </Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={loading || !status}
            className="bg-teal-700 hover:bg-teal-800"
            data-testid="workbench-submit-btn"
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Add to Workbench
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WorkbenchDialog;
